import { db, type LocalStaff } from "./db";

/**
 * Replicate authorizing staff onto the terminal.
 *
 * `ManagerAuth` verifies a manager's PIN against these records, which is what
 * lets a void, a refund, or an over-threshold cash variance be approved with no
 * network. Without this the approval dialog has nobody to offer and the action
 * simply cannot be taken — the failure we would hit at the end of a trading day
 * during an outage, which is the worst possible time to discover it.
 *
 * Called after sign-in, on every boot, and again by the approval dialog itself
 * if it finds the cache empty. It is an UPDATE to the cache, never a dependency:
 * if the fetch fails, whatever was replicated last time still works.
 *
 * WHY THIS REPORTS ITS OUTCOME instead of quietly returning a count. Sessions
 * last twelve hours and the client restores one by DECODING it, not verifying
 * it (AuthProvider explains why — verifying needs a key the client must not
 * hold). So a terminal left signed in overnight still looks signed in while
 * every authenticated request is refused. Swallowing that 401 here is what
 * turns a stale session into "No manager is set up on this terminal" hours
 * later, in the middle of a void, with no hint of the actual cause. The caller
 * gets the reason so it can say the true thing.
 */

interface CredentialRow {
  id: string;
  name: string;
  role: string;
  active: boolean;
  canAuthorize: boolean;
  pinHash: string;
  pinSalt: string;
  pinIterations: number;
}

export type RefreshOutcome =
  | { ok: true; count: number }
  /** The session was refused. Signing in again is the only cure. */
  | { ok: false; reason: "unauthenticated"; count: number }
  /** No line to the server. Whatever was replicated before still stands. */
  | { ok: false; reason: "unreachable"; count: number };

export async function refreshAuthorizers(sessionToken: string): Promise<RefreshOutcome> {
  let response: Response;

  try {
    response = await fetch("/api/staff/credentials", {
      headers: { authorization: `Bearer ${sessionToken}` },
    });
  } catch {
    return { ok: false, reason: "unreachable", count: await db.staff.count() };
  }

  if (response.status === 401 || response.status === 403) {
    return { ok: false, reason: "unauthenticated", count: await db.staff.count() };
  }

  if (!response.ok) {
    return { ok: false, reason: "unreachable", count: await db.staff.count() };
  }

  const rows = (await response.json()) as CredentialRow[];

  const records: LocalStaff[] = rows.map((row) => ({
    id: row.id,
    name: row.name,
    roleId: row.role,
    roleName: row.role,
    permissions: [],
    canAuthorize: true,
    active: row.active,
    pinHash: row.pinHash,
    pinSalt: row.pinSalt,
    pinIterations: row.pinIterations,
  }));

  await db.transaction("rw", db.staff, async () => {
    // Replace rather than merge: a manager who has been deactivated server-side
    // must stop being offerable here, and a stale row would keep them able to
    // approve corrections after their access was revoked.
    await db.staff.clear();
    if (records.length > 0) await db.staff.bulkPut(records);
  });

  return { ok: true, count: records.length };
}

/** The authorizing staff replicated onto this terminal. */
export async function cachedAuthorizers(): Promise<LocalStaff[]> {
  return db.staff.filter((s) => s.active && s.canAuthorize).toArray();
}
